import { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { ToastContainer } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import * as mixpanel from 'mixpanel-figma';
import posthog from 'posthog-js';

import 'react-toastify/dist/ReactToastify.css';

import './styles/customToast.css';
import './styles/App.scss';

import VersionUpdateBanner from 'Components/VersionUpdateBanner';

import { getReviewDataAction } from 'Redux/Actions/designReviewActions';
import { initializeFeedFiles } from 'Redux/Actions/FeedActions';
import { fetchSubscriptionInfo } from 'Redux/Actions/proActions';

import { readonlyRoutes, routes } from 'routes/routes';
import { RoutesWithSubRoutes } from 'routes/RoutesWithSubRoutes';

import { initAdora } from 'services/adora';
import { identifyUser } from 'services/eventTracking';

import { isUserInReadOnlyMode } from 'utlis/common';
import { NOTIFICATION_TYPES, REVIEWER_PLUGIN_AUTH_STATUS } from 'utlis/constants';
import { checkingUserType, logoutUser } from 'utlis/globalFunctions';
import {
  setAuthorizationToken,
  setProjectStoreDispatch,
} from 'utlis/http';
import { onSocketMessage } from 'utlis/socket';
import { infoReactToastify } from 'utlis/toasts';

import notificationStyles from './notification_styles.module.scss';

// disabling via config just in case
mixpanel.init(process.env.REACT_APP_MIXPANEL_ID, {
  disable_cookie: true,
  disable_persistence: true,
});

ReactGA.initialize(process.env.REACT_APP_GOOGLE_GTAG_KEY);

posthog.init(process.env.REACT_APP_POSTHOG_API_KEY, {
  api_host: 'https://us.i.posthog.com',
  defaults: '2026-05-30',
  person_profiles: 'identified_only',
  autocapture: true,
  enable_heatmaps: true,
  capture_performance: true,
  capture_pageview: true,
  disable_session_recording: false,
  session_recording: {
    maskAllInputs: (
      process.env.REACT_APP_X_BACKEND_URL !== 'staging'
      && process.env.REACT_APP_X_BACKEND_URL !== 'sandbox'),
  },
});

function App() {
  const userProfile = useSelector(state => state.userStore);
  const selectedProject = useSelector(state => state.selectedProject);
  const authUserId = useSelector(state => state.userStore?.uuid);
  const currentPlan = useSelector(state => state.proStore?.currentPlan);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const isReadonlyPage = isUserInReadOnlyMode();

  // URL params to convert dummy into design pro user
  const writeKey = searchParams.get('writeKey');
  const userID = searchParams.get('userID');
  const returnURL = searchParams.get('returnURL');

  const userObject = window.localStorage.getItem('userObject');

  // Logout user when user come from plugin to login design pro account
  useEffect(
    () => {
      if (writeKey && returnURL && userID && userObject) {
        const webURL = location.href;

        localStorage.setItem(
          'reviewerPluginAuth',
          JSON.stringify({
            status: REVIEWER_PLUGIN_AUTH_STATUS.FAILED,
            url: webURL,
          })
        );

        logoutUser(dispatch, navigate, true);
      }
    },
    [returnURL, userObject]
  );

  useEffect(() => {
    setAuthorizationToken();
    setProjectStoreDispatch(dispatch);
  }, []);

  useEffect(() => {
    if (!authUserId) {
      return;
    }

    dispatch(fetchSubscriptionInfo());
    dispatch(getReviewDataAction());
  }, [authUserId, dispatch]);

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: pathname,
      title: pathname,
    });
  }, [pathname]);

  useEffect(
    () => onSocketMessage(
      ({ title, body }) => {
        infoReactToastify(
          (
            <div className={notificationStyles['notification-wrapper']}>
              <span className={notificationStyles['notification-title']}>
                {title}
                :
              </span>
              <p dangerouslySetInnerHTML={{ __html: body }} />

            </div>
          ),
          {
            toastId: `${title}: ${body}`,
          }
        );
      },
      {
        excludeNotificationTypes: [
          NOTIFICATION_TYPES.FORM_EDIT,
          NOTIFICATION_TYPES.COMMENT_EDIT,
          NOTIFICATION_TYPES.COMMENT_DELETE,
          NOTIFICATION_TYPES.CREATE_NOTE,
          NOTIFICATION_TYPES.EDIT_NOTE,
          NOTIFICATION_TYPES.DELETE_NOTE,
          NOTIFICATION_TYPES.POST_VOTE,
          NOTIFICATION_TYPES.REACTION_ADDED,
          NOTIFICATION_TYPES.REACTION_MINUSED,
          NOTIFICATION_TYPES.MILESTONE_SEQUENCE_UPDATED,
        ],
      }
    ),
    [selectedProject]
  );
  useEffect(
    () => onSocketMessage(
      ({ data: { project_uuid } }) => {
        if (selectedProject?.uuid && project_uuid == selectedProject.uuid) {
          dispatch(initializeFeedFiles(selectedProject));
        }
      },
      {
        triggeredNotificationTypes: [
          NOTIFICATION_TYPES.MILESTONE_START,
          NOTIFICATION_TYPES.MILESTONE_APPROVE_BY_CLIENT,
          NOTIFICATION_TYPES.MILESTONE_DONE_BY_DESIGNER,
          NOTIFICATION_TYPES.FORM_ANSWERS_SUBMIT,
          NOTIFICATION_TYPES.FORM_SUBMIT,
          NOTIFICATION_TYPES.JOIN_PROJECT,
        ],
      }
    ),
    [selectedProject]
  );

  useEffect(() => {
    checkingUserType(userProfile, navigate);
  }, [userProfile, navigate]);

  useEffect(() => {
    identifyUser(userProfile.uuid, userProfile.email);
  }, [userProfile]);

  useEffect(() => {
    initAdora({ uid: authUserId, email: userProfile.email, plan: currentPlan });
  }, [authUserId, userProfile.email, currentPlan]);

  return (
    <>
      <RoutesWithSubRoutes
        routes={!isReadonlyPage ? routes : readonlyRoutes}
      />
      <ToastContainer
        bodyStyle={{ overflowWrap: 'anywhere' }}
        position="bottom-center"
        hideProgressBar
        style={{ width: 'max-content', maxWidth: '90vw' }}
      />
      <VersionUpdateBanner />
    </>
  );
}

export default App;
